import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Segmented,
  Space,
  Spin,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import NewsRow from '../../MarketExplorer/NewsRow';
import TagPalette from '../../MarketExplorer/TagPalette';
import { errorMessage } from '../../MarketExplorer/api';
import { sameTag } from '../../MarketExplorer/tags';
import {
  NewsArticle,
  NewsCuration,
  NewsTag,
  TagChoice,
} from '../../MarketExplorer/types';
import TagFilter from './TagFilter';
import { NewsSourceFilter, searchNews } from './api';
import { TagOption } from './types';

export const asArticle = (source: NewsCuration): NewsArticle => ({
  ...source,
  excerpt: source.body.slice(0, 300),
  curation_revision: source.revision,
});

const SOURCES: { label: string; value: NewsSourceFilter }[] = [
  { label: 'AI Curated', value: 'ai_curated' },
  { label: 'Originals', value: 'original' },
  { label: 'All', value: 'all' },
];

const SOURCE_HELP: Record<NewsSourceFilter, string> = {
  ai_curated:
    'AI Curated stories are clean copies of the originals: the headline is rewritten without dates, names or market moves, and the original stays unchanged. Filter them by tag, then add them to a blueprint from its News tab.',
  original:
    'Original PM articles. Edit them and apply shared tags here; articles used by protected blueprint feeds remain protected.',
  all: 'AI Curated copies and original PM articles together. A copy shows the headline of the original it was made from.',
};

const NewsBrowser: React.FC = () => {
  const [source, setSource] = useState<NewsSourceFilter>('ai_curated');
  const [tags, setTags] = useState<TagOption[]>([]);
  const [query, setQuery] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [rows, setRows] = useState<NewsArticle[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [choices, setChoices] = useState<TagChoice[]>([]);
  const [brush, setBrush] = useState<TagChoice | null>(null);
  const [editing, setEditing] = useState(false);
  const [reading, setReading] = useState<NewsArticle | null>(null);
  const request = useRef<AbortController | null>(null);
  const applied = useRef({
    q: '',
    date_start: '',
    date_end: '',
    source: 'ai_curated' as NewsSourceFilter,
    tag_ids: '',
  });
  const load = async (
    more = false,
    override?: Partial<typeof applied.current>,
  ) => {
    if (editing) return;
    if (!more && start && end && start > end) {
      setError('The end date must be after the start date.');
      return;
    }
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError('');
    if (!more)
      applied.current = {
        q: query.trim(),
        date_start: start,
        date_end: end,
        source,
        tag_ids: tags.map(tag => tag.id).join(','),
        ...override,
      };
    const filters = applied.current;
    try {
      const page = await searchNews(
        {
          q: filters.q,
          after_id: more ? next || 0 : 0,
          limit: 50,
          ...(filters.date_start ? { date_start: filters.date_start } : {}),
          ...(filters.date_end ? { date_end: filters.date_end } : {}),
          source: filters.source,
          ...(filters.tag_ids ? { tag_ids: filters.tag_ids } : {}),
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setRows(current =>
        more
          ? [...current, ...page.results.map(asArticle)]
          : page.results.map(asArticle),
      );
      setNext(page.next_after_id);
      setHasMore(page.has_more);
    } catch (reason) {
      if (!controller.signal.aborted) setError(errorMessage(reason));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    return () =>
      request.current?.abort(); /* Initial PM read never starts corpus work. */
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const addTag = (tag: NewsTag) =>
    setChoices(current =>
      current.some(value => sameTag(value, tag)) ? current : [...current, tag],
    );
  const changed = (source: NewsCuration) => {
    setRows(current =>
      current.map(row => (row.id === source.id ? asArticle(source) : row)),
    );
    setReading(current =>
      current?.id === source.id ? asArticle(source) : current,
    );
  };
  return (
    <section aria-label="Search and curate PM news">
      <form
        className="corpus-news__search"
        onSubmit={event => {
          event.preventDefault();
          void load();
        }}
      >
        <label className="corpus-news__query">
          Search news
          <Input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Headline or article text"
            maxLength={200}
            allowClear
            disabled={editing}
          />
        </label>
        <label>
          From
          <Input
            type="date"
            value={start}
            onChange={event => setStart(event.target.value)}
            disabled={editing}
          />
        </label>
        <label>
          To
          <Input
            type="date"
            value={end}
            onChange={event => setEnd(event.target.value)}
            disabled={editing}
          />
        </label>
        <Button
          htmlType="submit"
          icon={<SearchOutlined />}
          loading={loading}
          disabled={editing}
        >
          Search
        </Button>
      </form>
      <div className="corpus-news__filters">
        <Segmented
          aria-label="Which news to show"
          options={SOURCES}
          value={source}
          disabled={editing}
          onChange={value => {
            setSource(value as NewsSourceFilter);
            void load(false, { source: value as NewsSourceFilter });
          }}
        />
        <TagFilter
          value={tags}
          disabled={editing}
          onChange={value => {
            setTags(value);
            void load(false, { tag_ids: value.map(tag => tag.id).join(',') });
          }}
        />
      </div>
      <p className="corpus-news__muted">{SOURCE_HELP[source]}</p>
      <TagPalette
        choices={choices}
        active={brush}
        onSelect={setBrush}
        onAdd={addTag}
      />
      {error && <Alert type="error" showIcon title={error} />}
      {loading && !rows.length ? (
        <Spin />
      ) : !rows.length ? (
        <Empty
          description={
            source === 'ai_curated'
              ? 'No AI Curated news matches these filters.'
              : 'No news matches these filters.'
          }
        />
      ) : (
        <div className="market-explorer__news-list">
          {rows.map((item, index) => (
            <NewsRow
              key={item.id}
              item={item}
              index={index}
              brush={brush}
              onRead={setReading}
              onTagResolved={addTag}
              onChanged={changed}
              onEditing={setEditing}
              locked={editing}
            />
          ))}
        </div>
      )}
      {hasMore && (
        <Button
          onClick={() => load(true)}
          loading={loading}
          disabled={editing}
          className="corpus-news__load-more"
        >
          Load more news
        </Button>
      )}
      <Drawer
        title={reading?.headline || 'PM article'}
        open={Boolean(reading)}
        onClose={() => setReading(null)}
        size="large"
      >
        <Space orientation="vertical">
          <span className="corpus-news__muted">
            PM news #{reading?.id} ·{' '}
            {reading?.timestamp
              ? new Date(reading.timestamp).toLocaleString()
              : 'Publication time unavailable'}
          </span>
          <div className="corpus-news__article-body">{reading?.body}</div>
        </Space>
      </Drawer>
    </section>
  );
};
export default NewsBrowser;
